import { useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./create-inquiry.css";
import "../components/BackgroundMain.css";

// Override per-environment with REACT_APP_API_URL; falls back to production
// so a missing env var can never break the live form.
const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://aimonsite-02e4361db351.herokuapp.com";

const INQUIRIES_URL = `${API_BASE}/inquiries`;

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10MB — phone photos run large

// Returns a { fieldName: message } object; empty means the form is good to send.
const validate = (inquiry, otherInputs, file) => {
  const errors = {};

  if (!inquiry.name.trim()) {
    errors.name = "Please enter your name.";
  }

  const digits = inquiry.number.replace(/\D/g, "");
  if (!digits) {
    errors.number = "Please enter a phone number.";
  } else if (digits.length < 10) {
    errors.number = "Please enter a 10-digit phone number.";
  }

  if (!inquiry.email.trim()) {
    errors.email = "Please enter your email.";
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inquiry.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }

  if (!inquiry.typeOfService) {
    errors.typeOfService = "Please choose a type of service.";
  }

  if (!inquiry.productType) {
    errors.productType = "Please choose a product type.";
  } else if (
    inquiry.productType === "Other" &&
    !otherInputs.productTypeOther.trim()
  ) {
    errors.productTypeOther = "Please describe the product type.";
  }

  if (inquiry.brand === "Other" && !otherInputs.brandOther.trim()) {
    errors.brandOther = "Please enter the brand.";
  }

  if (file) {
    if (!file.type.startsWith("image/")) {
      errors.file = "Please upload an image file.";
    } else if (file.size > MAX_FILE_BYTES) {
      errors.file = "Image must be under 10MB.";
    }
  }

  return errors;
};

export const CreateInquiry = () => {
  const [inquiry, setInquiry] = useState({
    name: "",
    number: "",
    email: "",
    address: "",
    typeOfService: "",
    productType: "",
    brand: "",
    age: "",
    liftingMechanism: "",
    notes: "",
  });

  const [otherInputs, setOtherInputs] = useState({
    productTypeOther: "",
    brandOther: "",
  });

  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const navigate = useNavigate();
  const formRef = useRef(null);

  // Clear a field's error as soon as the visitor starts correcting it.
  const clearError = (name) => {
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setInquiry({ ...inquiry, [name]: value });
    clearError(name);
  };

  const handleOtherChange = (event) => {
    const { name, value } = event.target;
    setOtherInputs({ ...otherInputs, [name]: value });
    clearError(name);
  };

  const handleFileChange = (event) => {
    setFile(event.target.files[0] || null); // Set the selected file
    clearError("file");
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    const foundErrors = validate(inquiry, otherInputs, file);
    if (Object.keys(foundErrors).length > 0) {
      setErrors(foundErrors);
      // Move focus to the first problem so it isn't missed on a long form.
      const firstField = Object.keys(foundErrors)[0];
      formRef.current?.querySelector(`[name="${firstField}"]`)?.focus();
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    const formData = new FormData(); // Create a FormData object to handle form data

    // Append inquiry fields to the form data
    for (const key in inquiry) {
      // Use the custom value if "Other" was selected
      if (key === "productType" && inquiry[key] === "Other") {
        formData.append(key, otherInputs.productTypeOther.trim());
      } else if (key === "brand" && inquiry[key] === "Other") {
        formData.append(key, otherInputs.brandOther.trim());
      } else {
        formData.append(key, inquiry[key].trim());
      }
    }

    // Append the file to the form data
    if (file) {
      formData.append("file", file); // Append the file under the name "file"
    }

    try {
      await axios.post(INQUIRIES_URL, formData, {
        headers: {
          "Content-Type": "multipart/form-data", // Important for file uploads
        },
      });
      setShowSuccessPopup(true);
    } catch (err) {
      console.error(err);
      setSubmitError(
        "Sorry, we couldn't send your inquiry. Please try again, or text us at 650-862-5588."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHomeRedirect = () => {
    setShowSuccessPopup(false);
    navigate("/");
  };

  // Shared props for wiring a field up to its error message.
  const errorProps = (name) =>
    errors[name]
      ? { "aria-invalid": true, "aria-describedby": `${name}-error` }
      : {};

  const fieldError = (name) =>
    errors[name] ? (
      <div className="field-error" id={`${name}-error`} role="alert">
        {errors[name]}
      </div>
    ) : null;

  return (
    <div className="full-screen-div">
      <div className="main-container poppins-semibold">
        <h2>Create Inquiry</h2>
        <form onSubmit={onSubmit} ref={formRef} noValidate>
          <label htmlFor="name">
            Name<span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            autoComplete="name"
            onChange={handleChange}
            className={`input${errors.name ? " input-error" : ""}`}
            {...errorProps("name")}
          />
          {fieldError("name")}

          <label htmlFor="number">
            Number<span className="required">*</span>
          </label>
          <input
            type="tel"
            id="number"
            name="number"
            autoComplete="tel"
            onChange={handleChange}
            className={`input${errors.number ? " input-error" : ""}`}
            {...errorProps("number")}
          />
          {fieldError("number")}

          <label htmlFor="email">
            Email<span className="required">*</span>
          </label>
          <input
            type="email"
            id="email"
            name="email"
            autoComplete="email"
            onChange={handleChange}
            className={`input${errors.email ? " input-error" : ""}`}
            {...errorProps("email")}
          />
          {fieldError("email")}

          <label htmlFor="address">Address</label>
          <input
            type="text"
            id="address"
            name="address"
            autoComplete="street-address"
            onChange={handleChange}
            className="input"
          />

          <div className="dropdown-container">
            <div className="dropdown-wrapper">
              <label htmlFor="typeOfService">
                Type of Service<span className="required">*</span>
              </label>
              <select
                id="typeOfService"
                name="typeOfService"
                onChange={handleChange}
                className={errors.typeOfService ? "input-error" : ""}
                {...errorProps("typeOfService")}
              >
                <option value=""></option>
                <option value="In Home">In Home</option>
                <option value="Drop Off">Drop Off</option>
              </select>
              {fieldError("typeOfService")}
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="age">Age of Product</label>
              <select id="age" name="age" onChange={handleChange}>
                <option value=""></option>
                <option value="Less than 7 years">Less than 7 years</option>
                <option value="More than 7 years">More than 7 years</option>
              </select>
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="productType">
                Product Type<span className="required">*</span>
              </label>
              <select
                id="productType"
                name="productType"
                onChange={handleChange}
                className={errors.productType ? "input-error" : ""}
                {...errorProps("productType")}
              >
                <option value=""></option>
                <option value="Honeycomb/Cellular Shades">
                  Honeycomb/Cellular Shades
                </option>
                <option value="Silhouette Shades">Silhouette Shades</option>
                <option value="Wood Blinds">Wood Blinds</option>
                <option value="Roman Shades">Roman Shades</option>
                <option value="Metal Blinds">Metal Blinds</option>
                <option value="Other">Other</option>
              </select>
              {fieldError("productType")}
              {inquiry.productType === "Other" && (
                <>
                  <input
                    type="text"
                    id="productTypeOther"
                    name="productTypeOther"
                    placeholder="Enter custom product type"
                    onChange={handleOtherChange}
                    className={`input${
                      errors.productTypeOther ? " input-error" : ""
                    }`}
                    {...errorProps("productTypeOther")}
                  />
                  {fieldError("productTypeOther")}
                </>
              )}
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="brand">Brand</label>
              <select id="brand" name="brand" onChange={handleChange}>
                <option value=""></option>
                <option value="Hunter Douglas">Hunter Douglas</option>
                <option value="Levolor">Levolor</option>
                <option value="Bali">Bali</option>
                <option value="Graber">Graber</option>
                <option value="Other">Other</option>
              </select>
              {inquiry.brand === "Other" && (
                <>
                  <input
                    type="text"
                    id="brandOther"
                    name="brandOther"
                    placeholder="Enter custom brand"
                    onChange={handleOtherChange}
                    className={`input${errors.brandOther ? " input-error" : ""}`}
                    {...errorProps("brandOther")}
                  />
                  {fieldError("brandOther")}
                </>
              )}
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="liftingMechanism">Lifting Mechanism</label>
              <select
                id="liftingMechanism"
                name="liftingMechanism"
                onChange={handleChange}
                className="input"
              >
                <option value=""></option>
                <option value="Cordlock">Cordlock</option>
                <option value="EasyRise">EasyRise</option>
                <option value="LiteRise">LiteRise</option>
                <option value="UltraGlide">UltraGlide</option>
              </select>
            </div>

            <div className="dropdown-wrapper">
              <label htmlFor="file">Upload Image</label>
              <input
                type="file"
                id="file"
                name="file"
                accept="image/*"
                onChange={handleFileChange}
                className={`input${errors.file ? " input-error" : ""}`}
                {...errorProps("file")}
              />
              {fieldError("file")}
            </div>

            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              onChange={handleChange}
              className="input"
              placeholder="Enter any additional notes or requests here"
            />
          </div>
          <div className="button-inquiry-wrapper">
            <div className="inquiry-info">
              Inquiries and photos may also be sent via text message to
              650-862-5588*
            </div>
            <div className="button-container">
              <button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Sending..." : "Submit"}
              </button>
            </div>
          </div>
          {submitError && (
            <div className="submit-error" role="alert">
              {submitError}
            </div>
          )}
        </form>
      </div>
      {showSuccessPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>Thank you for filling out the form</h3>
            <h4>We will get back to you as soon as possible!</h4>
            <h5>For further inquiries, please contact aimonsite@gmail.com</h5>
            <button onClick={handleHomeRedirect}>Home</button>
          </div>
        </div>
      )}
    </div>
  );
};
